import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { adminApi } from '../lib/api';
import { useCompany } from '../contexts/CompanyContext';
import { startImpersonation } from '../lib/impersonation';
import { getTrialDaysRemaining } from '../lib/trial';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const STATUS_LABELS = {
  trial: 'Пробный период',
  active: 'Активна',
  suspended: 'Приостановлена',
  cancelled: 'Отменена',
  frozen: 'Заморожена',
};
const STATUS_VARIANTS = {
  trial: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-800',
  frozen: 'bg-gray-200 text-gray-800',
};

export default function AdminCompanies() {
  const navigate = useNavigate();
  const { selectCompany } = useCompany();
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const { data: companiesData, isLoading } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => adminApi.getCompanies(),
  });
  const { data: overviewData } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => adminApi.getOverview(),
  });
  const { data: auditData } = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: () => adminApi.getAuditLog(),
    enabled: showAuditLog,
  });

  const companies = companiesData?.data?.companies || [];
  const auditEntries = auditData?.data?.entries || [];
  const overview = overviewData?.data;

  const PLAN_LABELS = { free: 'Free', starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };

  const handleImpersonate = async (company) => {
    setImpersonatingId(company.id);
    try {
      const response = await adminApi.impersonate(company.id);
      startImpersonation(response.data.access_token, response.data.company);
      selectCompany(response.data.company);
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось войти как компания');
    } finally {
      setImpersonatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Компании на платформе</h1>
        <p className="text-gray-600">Все зарегистрированные компании, кроме Data Metrics</p>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-2xl font-bold text-gray-900" data-testid="overview-total-companies">{overview.total_companies}</p>
              <p className="text-xs text-gray-500 mt-1">Всего компаний</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-2xl font-bold text-gray-900">{overview.total_users}</p>
              <p className="text-xs text-gray-500 mt-1">Пользователей всего</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-2xl font-bold text-green-700">{overview.new_last_7d}</p>
              <p className="text-xs text-gray-500 mt-1">Новых за 7 дней</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-2xl font-bold text-green-700">{overview.new_last_30d}</p>
              <p className="text-xs text-gray-500 mt-1">Новых за 30 дней</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className={`text-2xl font-bold ${overview.trial_expiring_soon.length ? 'text-amber-600' : 'text-gray-900'}`}>
                {overview.trial_expiring_soon.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">Триал истекает ≤3 дн.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className={`text-2xl font-bold ${overview.needs_review_total ? 'text-amber-600' : 'text-gray-900'}`}>
                {overview.needs_review_total}
              </p>
              <p className="text-xs text-gray-500 mt-1">Требует проверки (документы)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {overview && (overview.trial_expiring_soon.length > 0 || Object.keys(overview.by_plan).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Тарифы клиентов</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {Object.entries(overview.by_plan).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{PLAN_LABELS[plan] || plan}</span>
                  <span className="font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Триал скоро закончится</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {overview.trial_expiring_soon.length === 0 ? (
                <p className="text-sm text-gray-500">Ни у кого триал не истекает в ближайшие 3 дня</p>
              ) : overview.trial_expiring_soon.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{c.name}</span>
                  <span className="font-medium text-amber-600">{c.days_left} дн.</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Компании ({companies.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-gray-500">Загрузка...</p>
          ) : companies.length === 0 ? (
            <p className="text-sm text-gray-500">Пока нет ни одной зарегистрированной компании</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Компания</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Триал</TableHead>
                  <TableHead>Пользователи</TableHead>
                  <TableHead>Проекты</TableHead>
                  <TableHead>Регистрация</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => {
                  const trialDays = getTrialDaysRemaining(company);
                  return (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>{company.subscription_plan || 'free'}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_VARIANTS[company.subscription_status] || ''}>
                          {STATUS_LABELS[company.subscription_status] || company.subscription_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {trialDays === null ? '—' : trialDays > 0 ? `${trialDays} дн.` : 'истёк'}
                      </TableCell>
                      <TableCell>{company.user_count}</TableCell>
                      <TableCell>{company.project_count}</TableCell>
                      <TableCell>
                        {company.created_at ? new Date(company.created_at).toLocaleDateString('ru-RU') : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleImpersonate(company)}
                          disabled={impersonatingId === company.id}
                          data-testid={`impersonate-${company.id}`}
                        >
                          {impersonatingId === company.id ? 'Входим...' : 'Войти как'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          className="cursor-pointer flex flex-row items-center justify-between"
          onClick={() => setShowAuditLog((v) => !v)}
        >
          <CardTitle className="text-base">История входов «как компания»</CardTitle>
          {showAuditLog ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CardHeader>
        {showAuditLog && (
          <CardContent>
            {auditEntries.length === 0 ? (
              <p className="text-sm text-gray-500">Записей пока нет</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Когда</TableHead>
                    <TableHead>Кто</TableHead>
                    <TableHead>В какую компанию</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{new Date(entry.started_at).toLocaleString('ru-RU')}</TableCell>
                      <TableCell>{entry.admin_email}</TableCell>
                      <TableCell>{entry.target_company_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
